import React, { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { useLanguage } from "../i18n/LanguageContext.tsx";
import { portfolioProjects as projects } from "../data/index.ts";

const ProjectsSection = () => {
  const { translations } = useLanguage();
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section id="projects" ref={sectionRef} className="py-20">
      <motion.div
        className="container mx-auto px-4 md:px-8"
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.6 }}
      >
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-dark-900 dark:text-white mb-4">
            {translations.projects.title}
          </h2>
          <motion.div
            className="w-20 h-1.5 bg-primary-500 rounded-full mx-auto"
            initial={{ width: 0 }}
            animate={isInView ? { width: 80 } : { width: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          />
          <p className="mt-4 text-dark-700 dark:text-dark-100 max-w-2xl mx-auto">
            {translations.projects.subtitle}
          </p>
        </div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          {projects.map((project) => {
            const item = translations.projects.items[project.key];
            return (
              <motion.div
                key={project.id}
                className="bg-white dark:bg-dark-800 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-100 dark:border-dark-700 group"
                variants={itemVariants}
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="relative h-48 overflow-hidden bg-gray-200 dark:bg-dark-700">
                  <img
                    src={`/images/projects/${project.image}`}
                    alt={item.imageAlt}
                    className="relative z-0 w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.onerror = null;
                      target.style.display = "none";
                      const parent = target.parentNode as HTMLElement;
                      if (parent && !parent.querySelector("[data-fallback]")) {
                        const fallback = document.createElement("div");
                        fallback.setAttribute("data-fallback", "true");
                        fallback.className =
                          "absolute inset-0 z-[1] flex items-center justify-center text-gray-400 dark:text-dark-600 font-heading font-bold text-2xl bg-gradient-to-br from-primary-500/10 to-secondary-500/10 pointer-events-none";
                        fallback.textContent = item.title;
                        const actions = parent.querySelector("[data-project-actions]");
                        if (actions) {
                          parent.insertBefore(fallback, actions);
                        } else {
                          parent.appendChild(fallback);
                        }
                      }
                    }}
                  />
                  <div
                    data-project-actions
                    className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/70 via-black/40 to-transparent p-4"
                  >
                    <div className="flex gap-2 justify-end">
                      <button
                        type="button"
                        onClick={() =>
                          setFullscreenImage(`/images/projects/${project.image}`)
                        }
                        className="relative z-20 w-9 h-9 bg-white/90 dark:bg-dark-800/90 rounded-full flex items-center justify-center text-primary-600 dark:text-primary-500 hover:bg-primary-500 hover:text-white transition-colors duration-300 cursor-pointer"
                        title={translations.projects.fullscreen}
                      >
                        <i className="fa-solid fa-expand text-sm pointer-events-none"></i>
                      </button>
                      {project.demoLink && (
                        <a
                          href={project.demoLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="relative z-20 w-9 h-9 bg-white/90 dark:bg-dark-800/90 rounded-full flex items-center justify-center text-primary-600 dark:text-primary-500 hover:bg-primary-500 hover:text-white transition-colors duration-300 cursor-pointer"
                          title={translations.projects.liveDemo}
                        >
                          <i className="fa-solid fa-eye text-sm pointer-events-none"></i>
                        </a>
                      )}
                      {project.codeLink && (
                        <a
                          href={project.codeLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="relative z-20 w-9 h-9 bg-white/90 dark:bg-dark-800/90 rounded-full flex items-center justify-center text-primary-600 dark:text-primary-500 hover:bg-primary-500 hover:text-white transition-colors duration-300 cursor-pointer"
                          title={translations.projects.viewSourceCode}
                        >
                          <i className="fa-brands fa-github text-sm pointer-events-none"></i>
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-5">
                  <span className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide">
                    {item.clientType}
                  </span>
                  <h3 className="text-lg font-heading font-bold text-dark-900 dark:text-white mt-1 mb-1">
                    {item.title}
                  </h3>
                  <p className="text-dark-700 dark:text-dark-200 mb-3 text-sm line-clamp-3">
                    {item.description}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {project.skills.map((skill) => (
                      <span
                        key={skill}
                        className="px-1.5 py-0.5 bg-gray-100 dark:bg-dark-700 text-dark-700 dark:text-dark-200 text-xs rounded-md"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <a
            href="https://github.com/taphacoobams?tab=repositories"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-dark-800 hover:bg-gray-100 dark:hover:bg-dark-700 text-dark-800 dark:text-white border border-gray-300 dark:border-dark-700 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
          >
            <i className="fa-brands fa-github"></i>
            {translations.projects.viewAllProjects}
          </a>
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {fullscreenImage && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setFullscreenImage(null)}
          >
            <motion.div
              className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center p-4"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <button
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors duration-300 z-10"
                onClick={() => setFullscreenImage(null)}
              >
                <i className="fa-solid fa-times text-xl"></i>
              </button>
              <img
                src={fullscreenImage}
                alt="Project preview"
                className="max-w-full max-h-full object-contain"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ProjectsSection;
